import React, { useEffect, useRef, useState } from 'react';
import './DobInput.css';

// Date of birth as three small number boxes (DD / MM / YYYY) instead of the
// phone's date picker, which opens on today's date — on mobile people had to
// scroll back decades and some couldn't type it at all. Each box brings up the
// number pad and moves on to the next once it's filled.
//
// value: 'YYYY-MM-DD' or ''. onChange(iso) fires only with a complete, real
// date (the parent does its own 18+ check, as before).

const pad = (n) => String(n).padStart(2, '0');

const splitIso = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  return m ? { d: m[3], m: m[2], y: m[1] } : { d: '', m: '', y: '' };
};

const realDate = (d, m, y) => {
  const day = Number(d); const month = Number(m); const year = Number(y);
  if (!day || !month || y.length !== 4) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  if (year < 1900 || date > new Date()) return null;
  return `${year}-${pad(month)}-${pad(day)}`;
};

const DobInput = ({ id, value, onChange, disabled }) => {
  const [parts, setParts] = useState(() => splitIso(value));
  const [error, setError] = useState('');
  const dayRef = useRef(null); const monthRef = useRef(null); const yearRef = useRef(null);
  const lastSent = useRef(value || '');

  // A new value from outside (profile loaded, saved) replaces what's shown.
  useEffect(() => {
    if ((value || '') !== lastSent.current) {
      lastSent.current = value || '';
      setParts(splitIso(value));
      setError('');
    }
  }, [value]);

  const update = (field, raw) => {
    const digits = raw.replace(/\D/g, '').slice(0, field === 'y' ? 4 : 2);
    const next = { ...parts, [field]: digits };
    setParts(next);

    // Jump on once a box can't take any more.
    if (field === 'd' && (digits.length === 2 || Number(digits) > 3)) monthRef.current?.focus();
    if (field === 'm' && (digits.length === 2 || Number(digits) > 1)) yearRef.current?.focus();

    const filled = next.d && next.m && next.y.length === 4;
    if (!filled) { setError(''); return; }
    const iso = realDate(next.d, next.m, next.y);
    if (!iso) { setError('Please enter a real date, e.g. 02 / 01 / 1963.'); return; }
    setError('');
    lastSent.current = iso;
    onChange(iso);
  };

  // Backspace in an empty box goes back to the one before.
  const back = (field) => (e) => {
    if (e.key !== 'Backspace' || parts[field]) return;
    if (field === 'm') dayRef.current?.focus();
    if (field === 'y') monthRef.current?.focus();
  };

  const box = (field, ref, placeholder, autoComplete, label) => (
    <input
      ref={ref}
      id={field === 'd' ? id : undefined}
      className={`dob-input-box dob-input-box--${field}`}
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      autoComplete={autoComplete}
      aria-label={label}
      placeholder={placeholder}
      value={parts[field]}
      onChange={(e) => update(field, e.target.value)}
      onKeyDown={back(field)}
      disabled={disabled}
    />
  );

  return (
    <div className="dob-input">
      <div className="dob-input-row">
        {box('d', dayRef, 'DD', 'bday-day', 'Day')}
        <span className="dob-input-sep">/</span>
        {box('m', monthRef, 'MM', 'bday-month', 'Month')}
        <span className="dob-input-sep">/</span>
        {box('y', yearRef, 'YYYY', 'bday-year', 'Year')}
      </div>
      {error && <p className="dob-input-error">{error}</p>}
    </div>
  );
};

export default DobInput;
